import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const AamiraReviews = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-80px' });

  const reviews = [
    {
      id: 1,
      text: "The craftsmanship is unlike anything I've seen at this price. I've owned European luxury pieces — Aamira Luxe holds its own with dignity.",
      name: 'Sophia Meera',
      meta: 'London · Statement Rings',
      initial: 'S',
      stars: 5,
    },
    {
      id: 2,
      text: "I gifted my mother a gold bangle from Aamira Luxe. She hasn't taken it off since. That kind of bond — between jewellery and memory — is priceless.",
      name: 'Amara Patel',
      meta: 'Mumbai · Heritage Bangles',
      initial: 'A',
      stars: 5,
    },
    {
      id: 3,
      text: "Elegant packaging, exquisite finish. The moment I opened the velvet box I knew this was something different. The earrings catch light like nothing I've experienced before.",
      name: 'Layla Hassan',
      meta: 'Cairo · Drop Earrings',
      initial: 'L',
      stars: 4,
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&display=swap');

        /* ── Section ── */
        .rv-section {
          background: #ffffff;
          padding: 100px clamp(24px, 6vw, 100px) 110px;
          position: relative;
          overflow: hidden;
        }

        .rv-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.025'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 0;
        }

        .rv-section::after {
          content: '';
          position: absolute;
          top: 0;
          left: clamp(24px, 6vw, 100px);
          right: clamp(24px, 6vw, 100px);
          height: 1px;
          background: linear-gradient(to right, transparent, #B08D57 30%, #B08D57 70%, transparent);
          opacity: 0.5;
        }

        .rv-bg-quote {
          position: absolute;
          top: 30px;
          right: clamp(24px, 8vw, 120px);
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(180px, 22vw, 320px);
          line-height: 1;
          color: #B08D57;
          opacity: 0.045;
          pointer-events: none;
          z-index: 0;
          user-select: none;
          font-weight: 700;
        }

        .rv-inner {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
        }

        /* ── Header ── */
        .rv-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          margin-bottom: 64px;
          gap: 24px;
          flex-wrap: wrap;
        }

        .rv-eyebrow {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #B08D57;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }
        .rv-eyebrow::before {
          content: '';
          width: 28px;
          height: 1px;
          background: #B08D57;
          display: inline-block;
          flex-shrink: 0;
        }

        .rv-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(36px, 4.5vw, 60px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1;
          letter-spacing: -0.02em;
        }
        .rv-title em {
          display: block;
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }

        .rv-badge {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 6px;
        }
        .rv-badge-stars { display: flex; gap: 4px; }
        .rv-star { color: #B08D57; font-size: 16px; }
        .rv-badge-label {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #9B9490;
        }
        .rv-badge-count {
          font-family: 'Cormorant Garamond', serif;
          font-size: 13px;
          font-style: italic;
          color: #B08D57;
          letter-spacing: 0.1em;
        }

        /* ── Grid ── */
        .rv-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        /* ── Card ── */
        .rv-card {
          background: #FAFAFA;
          border: 1px solid rgba(176,141,87,0.18);
          padding: 36px 32px 32px;
          position: relative;
          transition: border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
        }
        .rv-card:hover {
          background: #fff;
          border-color: rgba(176,141,87,0.5);
          box-shadow: 0 12px 40px rgba(176,141,87,0.10);
        }
        .rv-card::before {
          content: '';
          position: absolute;
          top: -1px;
          left: 24px;
          right: 24px;
          height: 2px;
          background: linear-gradient(to right, transparent, #B08D57, transparent);
          opacity: 0;
          transition: opacity 0.35s ease;
        }
        .rv-card:hover::before { opacity: 1; }

        .rv-card-quote-mark {
          font-family: 'Cormorant Garamond', serif;
          font-size: 56px;
          line-height: 0.7;
          color: #B08D57;
          opacity: 0.3;
          display: block;
          margin-bottom: 12px;
          font-weight: 300;
        }

        .rv-card-stars { display: flex; gap: 3px; margin-bottom: 20px; }
        .rv-card-star { font-size: 12px; color: #B08D57; }
        .rv-card-star-empty { font-size: 12px; color: #B08D57; opacity: 0.25; }

        .rv-card-text {
          font-family: 'Cormorant Garamond', serif;
          font-size: 17px;
          font-style: italic;
          font-weight: 300;
          color: #2E2B27;
          line-height: 1.75;
          letter-spacing: 0.01em;
          margin-bottom: 28px;
        }

        .rv-card-divider {
          width: 32px;
          height: 1px;
          background: linear-gradient(to right, #B08D57, transparent);
          margin-bottom: 20px;
        }

        .rv-card-author { display: flex; align-items: center; gap: 14px; }

        .rv-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #F0EDE8;
          border: 1px solid rgba(176,141,87,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Playfair Display', serif;
          font-size: 15px;
          font-style: italic;
          color: #B08D57;
          flex-shrink: 0;
          font-weight: 600;
        }

        .rv-author-name {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #1C1A18;
          margin-bottom: 3px;
        }

        .rv-author-meta {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 400;
          letter-spacing: 0.12em;
          color: #9B9490;
        }

        .rv-verified {
          position: absolute;
          top: 20px;
          right: 24px;
          font-family: 'Jost', sans-serif;
          font-size: 8px;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #B08D57;
          border: 1px solid rgba(176,141,87,0.35);
          padding: 4px 8px;
          opacity: 0.75;
        }

        /* ── Featured Card ── */
        .rv-featured {
          grid-column: span 3;
          background: #F9F6F1;
          border-color: rgba(176,141,87,0.3);
          padding: 52px 56px;
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 40px;
          align-items: center;
        }
        .rv-featured .rv-card-text {
          font-size: clamp(20px, 2.4vw, 26px);
          color: #1C1A18;
          line-height: 1.7;
          margin-bottom: 0;
        }

        .rv-featured-right {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          min-width: 120px;
        }
        .rv-featured-score {
          font-family: 'Playfair Display', serif;
          font-size: 64px;
          font-weight: 700;
          color: #B08D57;
          line-height: 1;
          letter-spacing: -0.03em;
        }
        .rv-featured-out {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #9B9490;
        }

        /* ── Ornament ── */
        .rv-ornament {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 60px;
          opacity: 0.35;
        }
        .rv-ornament span {
          font-family: 'Cormorant Garamond', serif;
          font-size: 11px;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: #B08D57;
          font-style: italic;
          white-space: nowrap;
        }
        .rv-ornament::before,
        .rv-ornament::after {
          content: '';
          flex: 1;
          height: 1px;
          background: linear-gradient(to right, transparent, #B08D57);
        }
        .rv-ornament::after {
          background: linear-gradient(to left, transparent, #B08D57);
        }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .rv-grid { grid-template-columns: 1fr; }
          .rv-featured {
            grid-column: span 1;
            grid-template-columns: 1fr;
            padding: 36px 28px;
            gap: 24px;
          }
          .rv-featured-right { flex-direction: row; align-items: center; }
          .rv-featured-score { font-size: 44px; }
        }
        @media (max-width: 600px) {
          .rv-section { padding: 70px clamp(20px, 5vw, 32px); }
          .rv-card { padding: 28px 22px 24px; }
          .rv-header { flex-direction: column; align-items: flex-start; }
          .rv-badge { align-items: flex-start; }
          .rv-featured { padding: 32px 22px; }
        }
      `}</style>

      <section className="rv-section" ref={sectionRef}>
        <div className="rv-bg-quote">"</div>

        <div className="rv-inner">

          {/* ── Header ── */}
          <div className="rv-header">
            <div>
              <motion.p
                className="rv-eyebrow"
                initial={{ opacity: 0, y: 16 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                Words of Our Patrons
              </motion.p>
              <motion.h2
                className="rv-title"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                Worn with <em>devotion.</em>
              </motion.h2>
            </div>

            <motion.div
              className="rv-badge"
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <div className="rv-badge-stars">
                {[...Array(5)].map((_, i) => <span key={i} className="rv-star">★</span>)}
              </div>
              <div className="rv-badge-label">Overall Experience</div>
              <div className="rv-badge-count">4.9 · 2,400+ Reviews</div>
            </motion.div>
          </div>

          {/* ── Reviews Grid ── */}
          <div className="rv-grid">

            {/* Featured Review */}
            <motion.div
              className="rv-card rv-featured"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <div>
                <div className="rv-card-stars">
                  {[...Array(5)].map((_, i) => <span key={i} className="rv-card-star">★</span>)}
                </div>
                <p className="rv-card-text">
                  "I wore my Aamira Luxe necklace on my wedding day. Every compliment I received
                  was for that single piece — it held the entire room in quiet awe. Jewellery
                  that doesn't just sit on you, but speaks for you."
                </p>
                <div className="rv-card-divider" />
                <div className="rv-card-author">
                  <div className="rv-avatar">N</div>
                  <div>
                    <div className="rv-author-name">Nadia Al-Farouqi</div>
                    <div className="rv-author-meta">Dubai · Bridal Collection</div>
                  </div>
                </div>
              </div>
              <div className="rv-featured-right">
                <div className="rv-featured-score">4.9</div>
                <div className="rv-badge-stars" style={{ gap: '3px' }}>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="rv-star" style={{ fontSize: '14px' }}>★</span>
                  ))}
                </div>
                <div className="rv-featured-out">Overall</div>
              </div>
            </motion.div>

            {/* Regular Review Cards */}
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                className="rv-card"
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 0.7,
                  delay: 0.35 + index * 0.12,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
              >
                <span className="rv-verified">Verified</span>
                <span className="rv-card-quote-mark">"</span>
                <div className="rv-card-stars">
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      className={i < review.stars ? 'rv-card-star' : 'rv-card-star-empty'}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <p className="rv-card-text">"{review.text}"</p>
                <div className="rv-card-divider" />
                <div className="rv-card-author">
                  <div className="rv-avatar">{review.initial}</div>
                  <div>
                    <div className="rv-author-name">{review.name}</div>
                    <div className="rv-author-meta">{review.meta}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* ── Ornament ── */}
          <motion.div
            className="rv-ornament"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 0.35 } : {}}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            <span>2,400+ Cherished Experiences</span>
          </motion.div>

        </div>
      </section>
    </>
  );
};

export default AamiraReviews;